import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import QRCode from 'qrcode.react';
import { toast } from 'react-toastify';
import { DadosListagemContasReceberCliente } from '../../types/ContaReceberTypes';
import { formatarDinheiro } from '../../helper';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import usePixPagamento from '../../Hooks/usePixPagamento';
import PixExpiracaoBar from '../../Components/generic/PixExpiracaoBar';

type Props = {
  onClose: () => void;
  dados: DadosListagemContasReceberCliente;
};

const COR_PIX = '#22c55e';
const COR_PIX_ESCURO = '#16a34a';

const INSTRUCOES = [
  { num: 1, texto: 'Abra o app do seu banco ou instituição financeira' },
  { num: 2, texto: 'Escolha "Pagar com QR Code" ou "Pix Copia e Cola"' },
  { num: 3, texto: 'Escaneie o QR Code ou cole o código copiado' }
];

const StepQrCodePix: React.FC<Props> = ({ onClose, dados }) => {
  const theme = useTheme();
  const [sessao] = useSessao();
  const [copiado, setCopiado] = useState(false);

  const { qrCode, carregando, dataExpiracao, tempoTotalSegundos, expirado, gerar } =
    usePixPagamento({
      dados,
      onEncerrar: onClose,
      carregandoInicial: true
    });

  useEffect(() => {
    gerar();
  }, []);

  const copiarQrCode = () => {
    navigator.clipboard.writeText(qrCode?.brcode ?? '');
    toast.info('QR code Copiado!');
    setCopiado(true);
    window.setTimeout(() => setCopiado(false), 2000);
  };

  if (carregando || !qrCode) {
    return (
      <Box
        sx={{
          py: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        <CircularProgress sx={{ color: COR_PIX }} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Gerando QR Code PIX...
        </Typography>
      </Box>
    );
  }

  if (expirado) {
    return (
      <Box sx={{ py: '24px', textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            mb: '20px',
            p: '16px',
            borderRadius: 3,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}
        >
          <Typography variant="body2" fontWeight={500} sx={{ color: 'warning.dark' }}>
            O QR Code PIX expirou. Gere um novo código para continuar o pagamento.
          </Typography>
        </Paper>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={gerar}
          sx={{
            py: '12px',
            px: '24px',
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: COR_PIX,
            boxShadow: `0 8px 16px -6px ${alpha(COR_PIX, 0.5)}`,
            '&:hover': { bgcolor: COR_PIX_ESCURO }
          }}
        >
          Gerar novo QR Code
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: '6px' }}>
      <Paper
        elevation={0}
        sx={{
          p: '12px',
          mb: '8px',
          bgcolor: alpha(COR_PIX, 0.04),
          borderRadius: 4,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" fontWeight={800} sx={{ color: 'text.primary' }}>
          R$ {formatarDinheiro(Number(qrCode?.valor.original ?? 0.0))}
        </Typography>
      </Paper>

      <Box sx={{ mb: '8px' }}>
        <PixExpiracaoBar
          dataExpiracao={dataExpiracao}
          tempoTotalSegundos={tempoTotalSegundos}
          variant="inline"
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: '12px',
          mb: '8px',
          bgcolor: 'white',
          borderRadius: 4,
          textAlign: 'center',
          boxShadow: `0 4px 24px ${alpha('#000', 0.08)}`,
          border: `1px solid ${alpha('#000', 0.06)}`
        }}
      >
        <QRCode
          value={qrCode.brcode}
          size={160}
          style={{ height: 'auto', borderRadius: '8px', margin: '0 auto' }}
        />
      </Paper>

      <Button
        fullWidth
        variant="contained"
        size="large"
        startIcon={copiado ? <CheckCircleIcon /> : <ContentCopyIcon />}
        onClick={copiarQrCode}
        sx={{
          py: '10px',
          mb: '12px',
          borderRadius: 3,
          textTransform: 'none',
          fontSize: 16,
          fontWeight: 600,
          bgcolor: copiado ? theme.palette.success.main : COR_PIX,
          boxShadow: `0 8px 16px -6px ${alpha(copiado ? theme.palette.success.main : COR_PIX, 0.5)}`,
          '&:hover': {
            bgcolor: copiado ? theme.palette.success.dark : COR_PIX_ESCURO
          },
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
        }}
      >
        {copiado ? 'Código copiado!' : 'Copiar código PIX'}
      </Button>

      <Box sx={{ mb: '12px' }}>
        {INSTRUCOES.map((item, index) => (
          <Stack
            key={item.num}
            direction="row"
            spacing="6px"
            alignItems="center"
            sx={{ mb: index < INSTRUCOES.length - 1 ? '6px' : 0 }}
          >
            <Avatar
              sx={{
                width: 20,
                height: 20,
                bgcolor: alpha(COR_PIX, 0.15),
                color: COR_PIX,
                fontSize: 11,
                fontWeight: 700
              }}
            >
              {item.num}
            </Avatar>
            <Typography variant="caption" color="text.secondary">
              {item.texto}
            </Typography>
          </Stack>
        ))}
      </Box>

      <Divider sx={{ mb: '8px' }} />

      <Typography
        variant="caption"
        color="text.disabled"
        display="block"
        textAlign="center"
      >
        para {sessao.empresa?.nome ?? ''}
      </Typography>
    </Box>
  );
};

export default StepQrCodePix;
